import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { debounce } from "lodash";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { WORKSPACE } from "@/constants/testIds";
import ProductTooltip, { ITEM_NUMBER_REGEX } from "./ProductTooltip";
import {
  Download,
  FileSpreadsheet,
  Maximize2,
  MousePointer2,
  Square,
  Circle as CircleIcon,
  ArrowRight,
  Type,
  Pencil as PenIcon,
  Eraser,
  Undo2,
  Redo2,
  Trash2,
  Share2,
  ZoomIn,
  ZoomOut,
  MoveHorizontal,
  MoveVertical,
  MoreHorizontal,
  X,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Image as ImageIcon,
  Edit3,
  Search,
  FilePlus,
  Layers,
  Copy,
  Boxes,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const AUTOSAVE_MS = 500;
const COLORS = [
  { key: "red", value: "#e11d48" },
  { key: "green", value: "#16a34a" },
  { key: "black", value: "#111111" },
];
const uid = () => Math.random().toString(36).slice(2, 10);

const TOOLS = ["select", "rect", "ellipse", "arrow", "text", "text-edit", "pen", "image", "eraser"];

export default function GridEditor({
  fileId,
  currentUser,
  onLockChange,
  focus = false,
  onToggleFocus,
  onRefresh,
}) {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("idle"); // idle|saving|saved
  const [tool, setTool] = useState("select");
  const [color, setColor] = useState(COLORS[0].value);
  const [strokeWidth, setStrokeWidth] = useState(3);
  const [shapes, setShapes] = useState([]);
  const [history, setHistory] = useState([[]]);
  const [historyIdx, setHistoryIdx] = useState(0);
  const [selectedId, setSelectedId] = useState(null);

  const [previewNonce, setPreviewNonce] = useState(0);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [containerWidth, setContainerWidth] = useState(1200);
  const [containerHeight, setContainerHeight] = useState(800);
  const [pageAspect, setPageAspect] = useState(null); // h / w of the first page
  const [productItem, setProductItem] = useState(null); // { itemNumber, anchor }
  const [zoom, setZoom] = useState(1); // 0.5 .. 3
  const containerRef = useRef(null);

  const fitWidth = useCallback(() => setZoom(1), []);
  const fitHeight = useCallback(() => {
    if (!pageAspect || !containerWidth || !containerHeight) return;
    // page rendered height = containerWidth * zoom * pageAspect ; want that ≈ containerHeight - padding
    const target = (containerHeight - 40) / (containerWidth * pageAspect);
    setZoom(Math.max(0.5, Math.min(3, +target.toFixed(2))));
  }, [pageAspect, containerWidth, containerHeight]);

  // ---- Load file + annotations ----
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [fRes, aRes] = await Promise.all([
        api.get(`/files/${fileId}`),
        api.get(`/files/${fileId}/annotations`).catch(() => ({ data: { shapes: [] } })),
      ]);
      setFile(fRes.data);
      const shp = aRes.data?.shapes || [];
      setShapes(shp);
      setHistory([shp]);
      setHistoryIdx(0);
      setSelectedId(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [fileId]);

  useEffect(() => {
    if (!fileId) return;
    load();
  }, [fileId, load]);

  // ---- Load PDF blob (polls while backend is generating) ----
  useEffect(() => {
    let cancelled = false;
    let currentUrl = null;
    let retryTimer = null;

    const attempt = async () => {
      try {
        const resp = await api.get(
          `/files/${fileId}/preview.pdf?v=${previewNonce}`,
          {
            responseType: "blob",
            // Accept 202 (still generating) as a valid response so axios
            // doesn't throw — we retry on it below.
            validateStatus: (s) => (s >= 200 && s < 300) || s === 202,
            timeout: 30000,
          }
        );
        if (cancelled) return;
        if (resp.status === 202) {
          // Backend is running LibreOffice conversion — poll again in 2s.
          retryTimer = setTimeout(attempt, 2000);
          return;
        }
        currentUrl = URL.createObjectURL(resp.data);
        setPdfBlobUrl(currentUrl);
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err));
      }
    };
    attempt();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [fileId, previewNonce]);

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const measure = () => {
      // On mobile give the PDF virtually all the horizontal space (only 6px
      // of side breathing room). On desktop keep the wider gutter.
      const isMobile = window.matchMedia("(max-width: 767px)").matches;
      const gutter = isMobile ? 6 : 32;
      setContainerWidth(el.clientWidth - gutter);
      setContainerHeight(el.clientHeight);
    };
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();
    // Also re-measure on viewport rotation / breakpoint change
    const mq = window.matchMedia("(max-width: 767px)");
    mq.addEventListener("change", measure);
    return () => {
      ro.disconnect();
      mq.removeEventListener("change", measure);
    };
  }, []);

  // ---- Pinch-to-zoom on touch devices ----
  const pinchRef = useRef(null); // { startDist, startZoom }
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const dist = (t1, t2) => {
      const dx = t1.clientX - t2.clientX;
      const dy = t1.clientY - t2.clientY;
      return Math.hypot(dx, dy);
    };
    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        pinchRef.current = {
          startDist: dist(e.touches[0], e.touches[1]),
          startZoom: zoom,
        };
      }
    };
    const onTouchMove = (e) => {
      if (e.touches.length === 2 && pinchRef.current) {
        e.preventDefault(); // stop the browser's native page-zoom
        const d = dist(e.touches[0], e.touches[1]);
        const ratio = d / pinchRef.current.startDist;
        const next = Math.max(0.5, Math.min(3, +(pinchRef.current.startZoom * ratio).toFixed(2)));
        setZoom(next);
      }
    };
    const onTouchEnd = (e) => {
      if (e.touches.length < 2) pinchRef.current = null;
    };
    // touchmove needs { passive: false } so preventDefault actually stops the
    // built-in browser gesture.
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [zoom]);

  // ---- Lazy react-pdf ----
  const [pdfLib, setPdfLib] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const mod = await import("react-pdf");
      // Load the pdfjs worker from a CDN using the exact bundled version.
      // Reason: some production hosts (including Emergent's static frontend
      // server for file.better-home.ca) serve `.mjs` files with the wrong
      // MIME type, which makes the browser refuse to execute the worker
      // module ("Setting up fake worker failed: Failed to fetch dynamically
      // imported module …/static/media/pdf.worker.min.HASH.mjs"). unpkg
      // returns the file with the correct `application/javascript` MIME.
      mod.pdfjs.GlobalWorkerOptions.workerSrc =
        `https://unpkg.com/pdfjs-dist@${mod.pdfjs.version}/build/pdf.worker.min.mjs`;
      await import("react-pdf/dist/esm/Page/AnnotationLayer.css");
      await import("react-pdf/dist/esm/Page/TextLayer.css");
      if (!cancelled) setPdfLib(mod);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- Persist annotations (debounced) ----
  const saveDebounced = useMemo(
    () =>
      debounce(
        async (shp) => {
          setStatus("saving");
          try {
            await api.put(`/files/${fileId}/annotations`, { shapes: shp });
            setStatus("saved");
            setTimeout(() => setStatus((s) => (s === "saved" ? "idle" : s)), 1500);
          } catch (err) {
            setStatus("idle");
            toast.error(formatApiError(err));
          }
        },
        AUTOSAVE_MS,
        // maxWait guarantees a save fires at least every 2s even when the user
        // is continuously drawing/typing without pausing (otherwise every new
        // commit would push the debounce timer forward and the save would
        // never actually fire).
        { maxWait: 2000 }
      ),
    [fileId]
  );

  // Flush any pending save when the file switches or the component unmounts,
  // so annotations aren't lost when the user quickly navigates between files.
  useEffect(() => {
    return () => {
      saveDebounced.flush();
    };
  }, [saveDebounced]);

  // Safety-net periodic flush: even if the debounce timing is somehow
  // delayed (busy tab, focus loss, etc.), guarantee that any pending
  // annotation change hits the DB at least once every 3 seconds. This is
  // what the user perceives as "auto-save every few seconds".
  useEffect(() => {
    const interval = setInterval(() => {
      try { saveDebounced.flush(); } catch { /* ignore */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [saveDebounced]);

  // Flush pending save when the browser tab is closed/refreshed. Uses
  // sendBeacon-compatible sync flush via the debounced fn's `flush()` (which
  // is synchronous invocation of the wrapped fn; the axios call becomes a
  // fetch-and-forget but the request leaves the tab before shutdown).
  useEffect(() => {
    const handler = () => {
      try {
        saveDebounced.flush();
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("beforeunload", handler);
    window.addEventListener("pagehide", handler);
    return () => {
      window.removeEventListener("beforeunload", handler);
      window.removeEventListener("pagehide", handler);
    };
  }, [saveDebounced]);

  const commit = useCallback(
    (nextShapes) => {
      setShapes(nextShapes);
      setHistory((h) => {
        const truncated = h.slice(0, historyIdx + 1);
        const next = [...truncated, nextShapes].slice(-60);
        return next;
      });
      setHistoryIdx((i) => Math.min(i + 1, 59));
      saveDebounced(nextShapes);
    },
    [historyIdx, saveDebounced]
  );

  // Insert an image as an annotation on the given page at position (x,y).
  // (x,y) are fractions-of-page-width in the same coordinate system used
  // elsewhere for shapes. Uploads to the backend for server-side
  // compression + storage, then appends the shape via `commit`.
  const insertImageAnnotation = useCallback(async (file, page, pos) => {
    if (!fileId || !file) return;
    try {
      const fd = new FormData();
      fd.append("file", file, file.name || "image.png");
      const { data } = await api.post(
        `/files/${fileId}/annotation-image`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const objectUrl = URL.createObjectURL(file);
      const dims = await new Promise((resolve) => {
        const im = new window.Image();
        im.onload = () => resolve({ w: im.naturalWidth, h: im.naturalHeight });
        im.onerror = () => resolve({ w: 300, h: 200 });
        im.src = objectUrl;
      });
      URL.revokeObjectURL(objectUrl);
      const defaultW = 0.3;
      const aspect = dims.w > 0 ? dims.h / dims.w : 0.75;
      // Center the image on the drop point (pos.x, pos.y), clamped so it
      // doesn't spill off the page.
      const x = Math.max(0, Math.min(1 - defaultW, (pos?.x ?? 0.5) - defaultW / 2));
      const y = Math.max(0, (pos?.y ?? 0.3) - (defaultW * aspect) / 2);
      const shape = {
        id: uid(),
        page: page || 1,
        type: "image",
        src: data.src,
        name: data.name,
        x,
        y,
        w: defaultW,
        h: defaultW * aspect,
      };
      // Read latest shapes via functional setter to avoid stale-closure when
      // dropping multiple images in one gesture.
      setShapes((prev) => {
        const next = [...prev, shape];
        // Also push a history entry + save debounced.
        setHistory((h) => {
          const truncated = h.slice(0, historyIdx + 1);
          return [...truncated, next].slice(-60);
        });
        setHistoryIdx((i) => Math.min(i + 1, 59));
        saveDebounced(next);
        return next;
      });
      setSelectedId(shape.id);
      return shape;
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [fileId, historyIdx, saveDebounced]);

  // Ctrl/⌘ + V paste: if the clipboard contains an image, upload it and drop
  // it onto page 1 at a reasonable default position. Ignored when the user is
  // typing inside an input/textarea/contenteditable.
  useEffect(() => {
    if (!fileId) return;
    const handler = async (e) => {
      const active = document.activeElement;
      if (
        active &&
        (active.tagName === "INPUT" ||
          active.tagName === "TEXTAREA" ||
          active.isContentEditable)
      ) {
        return;
      }
      const items = Array.from(e.clipboardData?.items || []);
      const imgItem = items.find((it) => (it.type || "").startsWith("image/"));
      if (!imgItem) return;
      const file = imgItem.getAsFile();
      if (!file) return;
      e.preventDefault();
      try {
        const fd = new FormData();
        // Clipboard File objects are usually named "image.png" — that's fine.
        fd.append("file", file, file.name || "pasted.png");
        const { data } = await api.post(
          `/files/${fileId}/annotation-image`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        // Measure natural dims to preserve aspect ratio.
        const objectUrl = URL.createObjectURL(file);
        const dims = await new Promise((resolve) => {
          const im = new window.Image();
          im.onload = () => resolve({ w: im.naturalWidth, h: im.naturalHeight });
          im.onerror = () => resolve({ w: 300, h: 200 });
          im.src = objectUrl;
        });
        URL.revokeObjectURL(objectUrl);
        const defaultW = 0.3;
        const aspect = dims.w > 0 ? dims.h / dims.w : 0.75;
        const shape = {
          id: uid(),
          page: 1,
          type: "image",
          src: data.src,
          name: data.name,
          x: 0.35,
          y: 0.3,
          w: defaultW,
          h: defaultW * aspect,
        };
        commit([...shapes, shape]);
        setSelectedId(shape.id);
        toast.success(t("image_pasted"));
      } catch (err) {
        toast.error(formatApiError(err));
      }
    };
    document.addEventListener("paste", handler);
    return () => document.removeEventListener("paste", handler);
  }, [fileId, shapes, commit, t]);

  const undo = useCallback(() => {
    if (historyIdx <= 0) return;
    const prev = history[historyIdx - 1];
    setShapes(prev);
    setHistoryIdx((i) => i - 1);
    saveDebounced(prev);
  }, [history, historyIdx, saveDebounced]);
  const redo = useCallback(() => {
    if (historyIdx >= history.length - 1) return;
    const next = history[historyIdx + 1];
    setShapes(next);
    setHistoryIdx((i) => i + 1);
    saveDebounced(next);
  }, [history, historyIdx, saveDebounced]);

  const deleteSelected = useCallback(() => {
    if (!selectedId) return;
    commit(shapes.filter((s) => s.id !== selectedId));
    setSelectedId(null);
  }, [selectedId, shapes, commit]);

  const clearAll = useCallback(() => {
    if (shapes.length === 0) return;
    if (!window.confirm(t("confirm_clear_annotations"))) return;
    commit([]);
    setSelectedId(null);
  }, [shapes.length, commit, t]);

  useEffect(() => {
    const handler = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if ((e.ctrlKey || e.metaKey) && (e.key === "z" || e.key === "Z")) {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      } else if ((e.ctrlKey || e.metaKey) && (e.key === "y" || e.key === "Y")) {
        e.preventDefault();
        redo();
      } else if ((e.key === "Delete" || e.key === "Backspace") && selectedId) {
        e.preventDefault();
        deleteSelected();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [undo, redo, selectedId, deleteSelected]);

  // ---- Actions ----
  const doDownloadAnnotated = async () => {
    try {
      const resp = await api.get(`/files/${fileId}/annotated.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = (file?.name || "file").replace(/\.[^.]+$/, "") + ".annotated.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const doShare = async () => {
    try {
      const { data } = await api.post(`/files/${fileId}/share`, {
        ttl_hours: 168,
        include_annotations: true,
      });
      const shareUrl = `${window.location.origin}/api/share/${data.token}`;
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success(t("share_copied"));
      } catch {
        toast.info(shareUrl);
      }
      // Also open a mailto: with pre-filled body
      const subject = encodeURIComponent(`Shared: ${file?.name || "document"}`);
      const body = encodeURIComponent(`${shareUrl}\n\n(Expires in 7 days)`);
      window.open(`mailto:?subject=${subject}&body=${body}`, "_blank");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // ---- Page-level operations (only meaningful on PDF-kind files) ----
  const isPdfKind = (file?.kind || file?.ext || "").toLowerCase().includes("pdf");
  const refreshPreview = () => {
    setShapes([]); // will be re-fetched by the annotation-load effect
    setPreviewNonce((n) => n + 1);
  };
  const doPageOp = async (opFn, successMsg) => {
    try {
      await opFn();
      // Re-fetch annotations after backend shifts them.
      try {
        const { data } = await api.get(`/files/${fileId}/annotations`);
        setShapes(data.shapes || []);
        setHistory([data.shapes || []]);
        setHistoryIdx(0);
      } catch { /* ignore */ }
      refreshPreview();
      if (successMsg) toast.success(successMsg);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const addBlankPage = () => doPageOp(
    () => api.post(`/files/${fileId}/pages`, { index: null }),
    "已新增页面"
  );
  const insertBlankAt = (idx0) => doPageOp(
    () => api.post(`/files/${fileId}/pages`, { index: idx0 }),
    "已插入空白页"
  );
  const deletePageAt = (idx0) => doPageOp(
    () => api.delete(`/files/${fileId}/pages/${idx0}`),
    "已删除页面"
  );
  const duplicatePageAt = (idx0) => doPageOp(
    () => api.post(`/files/${fileId}/pages/${idx0}/duplicate`),
    "已复制页面"
  );
  const appendFilesAsPages = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const fd = new FormData();
    for (const f of fileList) fd.append("files", f);
    return doPageOp(
      () => api.post(`/files/${fileId}/pages/append`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      }),
      "已追加到文档末尾"
    );
  };
  // Container-level drop: drop image/PDF files onto the editor to append.
  const [containerDragging, setContainerDragging] = useState(false);
  const onContainerDragOver = (e) => {
    // Only intercept when the drag payload is real files.
    if (Array.from(e.dataTransfer?.items || []).some((it) => it.kind === "file")) {
      e.preventDefault();
      setContainerDragging(true);
    }
  };
  const onContainerDragLeave = (e) => {
    // Only clear if truly leaving the container (relatedTarget outside).
    if (!e.currentTarget.contains(e.relatedTarget)) setContainerDragging(false);
  };
  const onContainerDrop = (e) => {
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length === 0) return;
    e.preventDefault();
    setContainerDragging(false);
    const images = [];
    const pdfs = [];
    for (const f of files) {
      const n = (f.name || "").toLowerCase();
      if (n.endsWith(".pdf")) pdfs.push(f);
      else if (/\.(png|jpg|jpeg|webp|gif|bmp)$/i.test(n)) images.push(f);
      else if ((f.type || "").startsWith("image/")) images.push(f);
    }
    if (images.length === 0 && pdfs.length === 0) {
      toast.error("只支持 PDF 或图片文件");
      return;
    }
    // ---- Images: insert as annotations at the drop position on the page
    // the pointer landed on. Hit-test against every rendered PDF page and
    // compute fraction-of-page-width coordinates (matching the storage
    // convention used by the annotation SVG).
    if (images.length > 0) {
      const dropClientX = e.clientX;
      const dropClientY = e.clientY;
      let targetPage = 1;
      let posOnPage = { x: 0.35, y: 0.3 };
      for (let p = 1; p <= numPages; p++) {
        const el = document.querySelector(`[data-testid="preview-page-${p}"]`);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (
          dropClientX >= r.left && dropClientX <= r.right &&
          dropClientY >= r.top && dropClientY <= r.bottom
        ) {
          targetPage = p;
          posOnPage = {
            x: (dropClientX - r.left) / r.width,
            y: (dropClientY - r.top) / r.width, // fraction-of-WIDTH — matches shape system
          };
          break;
        }
      }
      // Insert each image; stagger multiple drops so they don't stack.
      (async () => {
        let stagger = 0;
        for (const img of images) {
          await insertImageAnnotation(img, targetPage, {
            x: posOnPage.x + stagger,
            y: posOnPage.y + stagger,
          });
          stagger += 0.03;
        }
        if (images.length > 0) {
          toast.success(images.length === 1 ? "图片已插入" : `已插入 ${images.length} 张图片`);
        }
      })();
    }
    // ---- PDFs: append to the end of the document (unchanged behaviour).
    if (pdfs.length > 0) {
      appendFilesAsPages(pdfs);
    }
  };

  const [pageRailOpen, setPageRailOpen] = useState(false);
  const [activePageNumber, setActivePageNumber] = useState(1);
  const [compressOpen, setCompressOpen] = useState(false);

  // Track which page is currently at the top of the viewport so the page
  // rail can highlight the matching thumbnail as the user scrolls.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      const cRect = container.getBoundingClientRect();
      // Anchor point = 15% down the container. Whichever page's top has
      // just crossed above the anchor is the "current" page.
      const anchorY = cRect.top + cRect.height * 0.15;
      let best = 1;
      for (let p = 1; p <= numPages; p++) {
        const el = document.querySelector(`[data-testid="preview-page-${p}"]`);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (r.top <= anchorY) best = p;
        else break;
      }
      setActivePageNumber((prev) => (prev === best ? prev : best));
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    container.addEventListener("scroll", onScroll, { passive: true });
    // Run once to seed the initial value.
    update();
    return () => {
      container.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [numPages, pdfBlobUrl]);

  // Scroll the main preview so the given 1-based page number's top aligns
  // with the container's top. Uses manual `scrollTop` math to avoid
  // scrollIntoView quirks with nested scroll parents.
  const scrollToPage = useCallback((pageNum) => {
    setActivePageNumber(pageNum);
    const container = containerRef.current;
    if (!container) return;
    const el = document.querySelector(`[data-testid="preview-page-${pageNum}"]`);
    if (!el) {
      // Single-page focus mode (rail open): the target page hasn't rendered
      // yet, so just reset the scroll to the top and let the re-render show
      // the new page.
      container.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    // Continuous scroll mode: compute exact offset via offsetTop chain.
    let top = 0;
    let node = el;
    while (node && node !== container) {
      top += node.offsetTop;
      node = node.offsetParent;
    }
    container.scrollTo({
      top: Math.max(0, top - 4),
      behavior: "smooth",
    });
  }, []);

  if (loading || !file) {
    return <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">…</div>;
  }

  const statusPill = (() => {
    if (status === "saving") return { cls: "saving", label: t("saving") };
    if (status === "saved") return { cls: "saved", label: t("saved") };
    return null;
  })();

  return (
    <div data-testid={WORKSPACE.grid} className="flex-1 flex flex-col min-w-0 min-h-0 bg-background">
      <AnnotationToolbar
        file={file}
        focus={focus}
        statusPill={statusPill}
        onShare={doShare}
        onExport={doDownloadAnnotated}
        onToggleFocus={onToggleFocus}
        tool={tool}
        setTool={setTool}
        color={color}
        setColor={setColor}
        strokeWidth={strokeWidth}
        setStrokeWidth={setStrokeWidth}
        canUndo={historyIdx > 0}
        canRedo={historyIdx < history.length - 1}
        onUndo={undo}
        onRedo={redo}
        onClearAll={clearAll}
        onDelete={deleteSelected}
        hasSelection={!!selectedId}
        zoom={zoom}
        setZoom={setZoom}
        fitWidth={fitWidth}
        fitHeight={fitHeight}
        onLookupProduct={(itemNumber, rect) => {
          setProductItem({
            itemNumber,
            anchor: {
              top: rect.top,
              bottom: rect.bottom,
              left: rect.left,
              right: rect.right,
            },
          });
        }}
        isPdfKind={isPdfKind}
        onAddPage={addBlankPage}
        pageRailOpen={pageRailOpen}
        onTogglePageRail={() => setPageRailOpen((v) => !v)}
        onOpenCompress={() => setCompressOpen(true)}
        currentKind={file?.kind || ""}
      />

      <div
        ref={containerRef}
        className="flex-1 min-h-0 relative bg-neutral-100 overflow-auto"
        data-testid="preview-frame"
        // `pan-x pan-y` allows normal one-finger scrolling but tells the
        // browser NOT to interpret two-finger gestures as its own page zoom
        // — so our JS pinch handler owns the gesture.
        style={{ touchAction: "pan-x pan-y" }}
        onDragOver={onContainerDragOver}
        onDragLeave={onContainerDragLeave}
        onDrop={onContainerDrop}
      >
        {containerDragging && (
          <div
            className="pointer-events-none absolute inset-4 z-20 rounded-lg border-2 border-dashed border-[hsl(var(--brand))] bg-[hsl(var(--brand-tint))]/40 flex items-center justify-center text-sm font-semibold text-[hsl(var(--brand))]"
            data-testid="page-drop-overlay"
          >
            松开鼠标 · 图片插入到该位置 · PDF 追加为新页
          </div>
        )}
        {pageRailOpen && numPages > 0 && pdfLib && pdfBlobUrl && (
          <PageThumbnailRail
            pdfLib={pdfLib}
            file={pdfBlobUrl}
            numPages={numPages}
            activePage={activePageNumber}
            onGoto={scrollToPage}
            onDelete={(idx0) => deletePageAt(idx0)}
            onDuplicate={(idx0) => duplicatePageAt(idx0)}
            onInsertAfter={(idx0) => insertBlankAt(idx0 + 1)}
            onClose={() => setPageRailOpen(false)}
          />
        )}
        {(!pdfLib || !pdfBlobUrl) && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-10 text-sm font-mono uppercase tracking-widest text-muted-foreground pointer-events-none">
            {t("preview_loading")}
          </div>
        )}
        {pdfLib && pdfBlobUrl && (
          <div className={`flex flex-col items-center py-1 sm:py-4 gap-2 sm:gap-4 min-w-max mx-auto transition-[padding] ${pageRailOpen ? "pl-44" : ""}`}>
            <pdfLib.Document
              file={pdfBlobUrl}
              onLoadSuccess={(info) => setNumPages(info.numPages)}
              onLoadError={(e) => toast.error(String(e?.message || e))}
              loading={<div className="text-sm font-mono text-muted-foreground">…</div>}
            >
              {Array.from({ length: numPages }, (_, i) => i + 1)
                .filter((p) => !pageRailOpen || p === activePageNumber)
                .map((p) => (
                <PageWithAnnotations
                  key={p}
                  PageComp={pdfLib.Page}
                  pageNumber={p}
                  width={Math.max(containerWidth, 240) * zoom}
                  tool={tool}
                  color={color}
                  strokeWidth={strokeWidth}
                  shapes={shapes}
                  onCommit={commit}
                  selectedId={selectedId}
                  setSelectedId={setSelectedId}
                  fileId={fileId}
                  onItemLookup={(itemNumber, rect) => {
                    setProductItem({
                      itemNumber,
                      anchor: {
                        top: rect.top,
                        bottom: rect.bottom,
                        left: rect.left,
                        right: rect.right,
                      },
                    });
                  }}
                  onFirstPageRender={
                    p === 1
                      ? (nw, nh) => setPageAspect(nh && nw ? nh / nw : null)
                      : undefined
                  }
                />
              ))}
            </pdfLib.Document>
          </div>
        )}
      </div>
      {productItem && (
        <ProductTooltip
          itemNumber={productItem.itemNumber}
          anchor={productItem.anchor}
          onClose={() => setProductItem(null)}
        />
      )}
      {compressOpen && (
        <CompressDialog
          fileId={fileId}
          originalSize={file?.size || 0}
          flushPendingSaves={async () => {
            // Ensure the latest annotations are persisted BEFORE compression
            // reads them from the DB. Without this the user's just-drawn
            // shape/image can be lost if they compress within the debounce
            // window (500 ms – 2 s).
            try {
              saveDebounced.flush();
              // Give the flush's async PUT time to hit the DB.
              await new Promise((r) => setTimeout(r, 350));
            } catch { /* ignore */ }
          }}
          onClose={() => setCompressOpen(false)}
          onApplied={() => {
            setCompressOpen(false);
            refreshPreview();
            onRefresh?.();
          }}
        />
      )}
    </div>
  );
}

// ---------- Compress dialog ----------
function formatBytes(n) {
  if (!n || n < 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : v >= 10 ? 1 : 2)} ${units[i]}`;
}

const COMPRESS_LEVELS = [
  { key: "low",    label: "低压缩",  desc: "150 DPI · 高保真",   },
  { key: "medium", label: "中等",    desc: "100 DPI · 平衡",     },
  { key: "high",   label: "高压缩",  desc: "72 DPI · 最小体积",  },
];

function CompressDialog({ fileId, originalSize, flushPendingSaves, onClose, onApplied }) {
  const [level, setLevel] = useState("medium");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { token, original_size, compressed_size, reduction_pct }
  const [applying, setApplying] = useState(false);

  const runCompress = async () => {
    setBusy(true);
    try {
      // Persist any pending annotation edits before the backend bakes them in.
      await flushPendingSaves?.();
      const { data } = await api.post(`/files/${fileId}/compress`, { level });
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const doDownload = async () => {
    if (!result) return;
    try {
      const resp = await api.get(
        `/files/${fileId}/compress/${result.token}/download`,
        { responseType: "blob" }
      );
      const cd = resp.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : "compressed.pdf";
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("已开始下载");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const doApply = async () => {
    if (!result || applying) return;
    setApplying(true);
    try {
      await api.post(`/files/${fileId}/compress/${result.token}/apply`);
      toast.success("已替换为压缩版");
      onApplied?.();
    } catch (err) {
      toast.error(formatApiError(err));
      setApplying(false);
    }
  };

  const reduction = result?.reduction_pct ?? 0;
  const reductionColor = reduction >= 20 ? "text-green-600" : reduction > 0 ? "text-amber-600" : "text-red-600";

  return (
    <div
      data-testid="compress-dialog-backdrop"
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div
        data-testid="compress-dialog"
        className="w-full max-w-md bg-white rounded-lg shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 h-11 border-b border-border">
          <div className="flex items-center gap-2">
            <Boxes className="w-4 h-4 text-primary" strokeWidth={1.75} />
            <span className="text-sm font-semibold">压缩文档</span>
          </div>
          <button
            data-testid="compress-dialog-close"
            onClick={onClose}
            className="h-7 w-7 flex items-center justify-center rounded-sm hover:bg-accent"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="text-xs text-muted-foreground">
            压缩会将 PDF 每页栅格化为图像 + 把您的标注（图片 / 文字 / 图形）烘焙进页面。这样下载或替换后的文件包含<b className="text-foreground">编辑器里看到的所有内容</b>，但<b className="text-foreground">文字将无法再选中/复制</b>。
          </div>

          {/* Level chooser */}
          <div className="space-y-2">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              压缩级别
            </div>
            <div className="grid grid-cols-3 gap-2">
              {COMPRESS_LEVELS.map((L) => (
                <button
                  key={L.key}
                  data-testid={`compress-level-${L.key}`}
                  disabled={busy || !!result}
                  onClick={() => setLevel(L.key)}
                  className={`px-2 py-2 rounded-sm border text-left transition-colors ${
                    level === L.key
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  } ${(busy || result) ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <div className="text-xs font-semibold">{L.label}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">{L.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Size numbers */}
          <div className="rounded-sm border border-border/60 bg-muted/30 p-3 space-y-2">
            <div className="flex items-baseline justify-between text-xs">
              <span className="text-muted-foreground">原始大小</span>
              <span
                data-testid="compress-original-size"
                className="font-mono font-semibold"
              >
                {formatBytes(originalSize)}
              </span>
            </div>
            {result && (
              <>
                <div className="flex items-baseline justify-between text-xs">
                  <span className="text-muted-foreground">压缩后大小</span>
                  <span
                    data-testid="compress-new-size"
                    className="font-mono font-semibold"
                  >
                    {formatBytes(result.compressed_size)}
                  </span>
                </div>
                <div className="flex items-baseline justify-between text-xs">
                  <span className="text-muted-foreground">体积变化</span>
                  <span
                    data-testid="compress-reduction"
                    className={`font-mono font-bold ${reductionColor}`}
                  >
                    {reduction > 0 ? `↓ ${reduction.toFixed(1)}%` : reduction < 0 ? `↑ ${Math.abs(reduction).toFixed(1)}%` : "0%"}
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Actions */}
          {!result ? (
            <button
              data-testid="compress-run-btn"
              disabled={busy}
              onClick={runCompress}
              className="w-full h-9 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-50"
            >
              {busy ? "正在压缩…" : "开始压缩"}
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                data-testid="compress-download-btn"
                onClick={doDownload}
                className="flex-1 h-9 rounded-sm border border-border text-sm font-semibold hover:bg-accent"
              >
                下载压缩版
              </button>
              <button
                data-testid="compress-apply-btn"
                disabled={applying}
                onClick={doApply}
                className="flex-1 h-9 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-50"
              >
                {applying ? "替换中…" : "替换原文件"}
              </button>
            </div>
          )}

          {result && reduction < 0 && (
            <div className="text-[11px] text-red-600 leading-tight">
              提示：此级别导致体积变大（原文件本身已经很紧凑），可试试"高压缩"或直接取消。
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Page thumbnails rail (left overlay panel) ----------
function PageThumbnailRail({ pdfLib, file, numPages, activePage, onGoto, onDelete, onDuplicate, onInsertAfter, onClose }) {
  const listRef = useRef(null);
  // Whenever the active page changes, scroll the rail so the active thumb
  // is visible. We compute scrollTop manually — `scrollIntoView({block:'nearest'})`
  // is unreliable with nested scroll parents (the rail's own overflow-y-auto
  // AND the outer preview-frame overflow-auto): the browser sometimes
  // decides the element is 'already nearest' and refuses to scroll.
  useEffect(() => {
    const list = listRef.current;
    const el = list?.querySelector(`[data-testid="page-thumb-${activePage}"]`);
    if (!list || !el) return;
    const cr = list.getBoundingClientRect();
    const er = el.getBoundingClientRect();
    // Only scroll if the thumb is not fully within the visible list.
    if (er.top >= cr.top && er.bottom <= cr.bottom) return;
    // Center the thumb vertically in the list viewport.
    const delta = er.top - cr.top - (cr.height / 2 - er.height / 2);
    list.scrollTo({ top: list.scrollTop + delta, behavior: "smooth" });
  }, [activePage]);

  return (
    <div
      data-testid="page-thumb-rail"
      className="absolute top-2 left-2 bottom-2 z-20 w-40 rounded-lg border border-border bg-white shadow-lg flex flex-col overflow-hidden"
    >
      <div className="flex items-center justify-between px-2 h-8 border-b border-border/60 bg-muted/40">
        <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
          页面 · {numPages}
        </span>
        <button
          data-testid="page-thumb-rail-close"
          onClick={onClose}
          title="关闭"
          className="h-6 w-6 flex items-center justify-center rounded-sm hover:bg-accent"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div ref={listRef} className="flex-1 overflow-y-auto px-2 py-2 space-y-2">
        <pdfLib.Document file={file} loading={null}>
          {Array.from({ length: numPages }, (_, i) => (
            <PageThumbItem
              key={i}
              PageComp={pdfLib.Page}
              pageNumber={i + 1}
              index={i}
              isActive={i + 1 === activePage}
              onGoto={onGoto}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
              onInsertAfter={onInsertAfter}
            />
          ))}
        </pdfLib.Document>
      </div>
    </div>
  );
}

function PageThumbItem({ PageComp, pageNumber, index, isActive, onGoto, onDelete, onDuplicate, onInsertAfter }) {
  return (
    <div
      data-testid={`page-thumb-${pageNumber}`}
      data-active={isActive ? "true" : "false"}
      className={`relative group border-2 rounded-sm overflow-hidden bg-white cursor-pointer transition-colors ${
        isActive
          ? "border-primary shadow-[0_0_0_2px_hsl(var(--brand)/0.15)]"
          : "border-border/60 hover:border-primary/60"
      }`}
      onClick={() => onGoto(pageNumber)}
    >
      <div className="pointer-events-none">
        <PageComp
          pageNumber={pageNumber}
          width={130}
          renderTextLayer={false}
          renderAnnotationLayer={false}
        />
      </div>
      <div className={`absolute inset-x-0 bottom-0 h-5 text-[10px] font-mono flex items-center justify-center ${
        isActive ? "bg-primary text-primary-foreground" : "bg-black/70 text-white"
      }`}>
        {pageNumber}
      </div>
      {/* Hover actions */}
      <div className="absolute top-1 right-1 hidden group-hover:flex items-center gap-0.5 bg-white/95 rounded-sm p-0.5 shadow">
        <button
          data-testid={`page-thumb-duplicate-${pageNumber}`}
          onClick={(e) => { e.stopPropagation(); onDuplicate(index); }}
          title="复制此页"
          className="h-5 w-5 flex items-center justify-center rounded-sm hover:bg-accent"
        >
          <Copy className="w-3 h-3" strokeWidth={2} />
        </button>
        <button
          data-testid={`page-thumb-insert-${pageNumber}`}
          onClick={(e) => { e.stopPropagation(); onInsertAfter(index); }}
          title="在此页后插入空白页"
          className="h-5 w-5 flex items-center justify-center rounded-sm hover:bg-accent"
        >
          <FilePlus className="w-3 h-3" strokeWidth={2} />
        </button>
        <button
          data-testid={`page-thumb-delete-${pageNumber}`}
          onClick={(e) => { e.stopPropagation(); onDelete(index); }}
          title="删除此页"
          className="h-5 w-5 flex items-center justify-center rounded-sm hover:bg-red-100 text-red-600"
        >
          <Trash2 className="w-3 h-3" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}

// ---------- Manual product-lookup input (used inside the toolbar) ----------
function ProductLookupInput({ onSubmit }) {
  const [value, setValue] = useState("");
  const wrapRef = useRef(null);
  const submit = () => {
    const q = value.trim();
    if (!q) return;
    const rect = wrapRef.current?.getBoundingClientRect() || { top: 60, bottom: 90, left: 200, right: 400 };
    onSubmit?.(q, rect);
  };
  return (
    <div
      ref={wrapRef}
      className="hidden md:flex items-center gap-1 flex-shrink-0 h-8 pl-2 pr-1 rounded-sm border border-border/70 bg-background focus-within:border-primary transition-colors"
      data-testid="product-lookup-wrap"
    >
      <Search className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.75} />
      <input
        data-testid="product-lookup-input"
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value.toUpperCase())}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
        placeholder="查询产品编号"
        className="h-6 w-32 lg:w-40 bg-transparent text-xs font-mono outline-none placeholder:text-muted-foreground/60 tracking-wide"
      />
      <button
        data-testid="product-lookup-submit"
        onClick={submit}
        disabled={!value.trim()}
        title="查询产品资料"
        className="h-6 px-2 text-[11px] font-medium rounded-sm bg-primary text-primary-foreground disabled:opacity-40 hover:bg-primary/90 transition-colors"
      >
        查询
      </button>
    </div>
  );
}

// ---------- Toolbar ----------
function AnnotationToolbar({
  file,
  focus,
  statusPill,
  onShare,
  onExport,
  onToggleFocus,
  tool,
  setTool,
  color,
  setColor,
  strokeWidth,
  setStrokeWidth,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onClearAll,
  onDelete,
  hasSelection,
  zoom,
  setZoom,
  fitWidth,
  fitHeight,
  onLookupProduct,
  isPdfKind,
  onAddPage,
  pageRailOpen,
  onTogglePageRail,
  onOpenCompress,
}) {
  const { t } = useTranslation();
  // Primary toolbar shows the most-used tools directly. Rectangle and Pen
  // moved out to the overflow "More tools" popover per user request; Image
  // and Eraser promoted into the primary row.
  const primaryTools = [
    { id: "select", label: t("tool_select"), Icon: MousePointer2 },
    { id: "text", label: t("tool_text"), Icon: Type },
    { id: "text-edit", label: t("tool_text_edit"), Icon: Edit3 },
    { id: "image", label: t("tool_image"), Icon: ImageIcon },
    { id: "eraser", label: t("tool_eraser"), Icon: Eraser },
  ];
  const overflowTools = [
    { id: "rect", label: t("tool_rect"), Icon: Square },
    { id: "ellipse", label: t("tool_circle"), Icon: CircleIcon },
    { id: "arrow", label: t("tool_arrow"), Icon: ArrowRight },
    { id: "pen", label: t("tool_pen"), Icon: PenIcon },
  ];
  const toolBtn = ({ id, label, Icon }, sizeCls = "h-8 w-8") => (
    <button
      key={id}
      data-testid={`tool-${id}`}
      onClick={() => setTool(id)}
      title={label}
      className={`${sizeCls} flex items-center justify-center rounded-sm tx-bg ${
        tool === id
          ? "bg-primary text-primary-foreground"
          : "text-foreground hover:bg-accent"
      }`}
    >
      <Icon className="w-4 h-4" strokeWidth={1.75} />
    </button>
  );

  return (
    <div
      data-testid="annotation-toolbar"
      className="flex items-center gap-1 px-2 sm:px-3 h-10 sm:h-11 border-b border-border/60 bg-muted/20 flex-nowrap overflow-x-auto"
    >
      {/* File name + status (hidden on mobile to save room) */}
      {!focus && file && (
        <>
          <FileSpreadsheet className="w-4 h-4 text-muted-foreground flex-shrink-0 hidden sm:block" strokeWidth={1.5} />
          <div className="font-semibold text-sm truncate max-w-[140px] sm:max-w-[220px] hidden sm:block" title={file.name}>
            {file.name}
          </div>
          {statusPill && (
            <span data-testid={WORKSPACE.gridStatus} className={`pill ${statusPill.cls} flex-shrink-0 hidden sm:inline-flex`}>
              <span className="pill-dot" />
              {statusPill.label}
            </span>
          )}
          <div className="w-px h-6 bg-border mx-1 flex-shrink-0 hidden sm:block" />
        </>
      )}

      {/* Primary tools */}
      {primaryTools.map((tt) => toolBtn(tt))}
      <div className="w-px h-6 bg-border mx-1 flex-shrink-0" />

      {/* Colors */}
      {COLORS.map((c) => (
        <button
          key={c.key}
          data-testid={`color-${c.key}`}
          onClick={() => setColor(c.value)}
          title={t(`color_${c.key}`)}
          className={`h-8 w-8 flex items-center justify-center rounded-sm tx-bg ${
            color === c.value ? "ring-2 ring-primary" : "hover:bg-accent"
          }`}
        >
          <span className="w-4 h-4 rounded-full" style={{ background: c.value }} />
        </button>
      ))}
      <div className="w-px h-6 bg-border mx-1 flex-shrink-0" />

      {/* Undo / Redo */}
      <button
        data-testid="tool-undo"
        onClick={onUndo}
        disabled={!canUndo}
        title={`${t("tool_undo")} (Ctrl+Z)`}
        className="h-8 w-8 flex items-center justify-center rounded-sm hover:bg-accent disabled:opacity-40 tx-bg"
      >
        <Undo2 className="w-4 h-4" strokeWidth={1.75} />
      </button>
      <button
        data-testid="tool-redo"
        onClick={onRedo}
        disabled={!canRedo}
        title={`${t("tool_redo")} (Ctrl+Shift+Z)`}
        className="h-8 w-8 flex items-center justify-center rounded-sm hover:bg-accent disabled:opacity-40 tx-bg"
      >
        <Redo2 className="w-4 h-4" strokeWidth={1.75} />
      </button>
      <div className="w-px h-6 bg-border mx-1 flex-shrink-0" />

      {/* Zoom */}
      <div className="inline-flex items-center gap-0.5 flex-shrink-0">
        <button
          data-testid="zoom-out"
          onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(2)))}
          title={t("zoom_out")}
          className="h-8 w-8 flex items-center justify-center rounded-sm hover:bg-accent tx-bg"
        >
          <ZoomOut className="w-4 h-4" strokeWidth={1.75} />
        </button>
        <button
          data-testid="zoom-fit"
          onClick={() => setZoom(1)}
          title={t("reset_zoom")}
          className="h-8 px-1 flex items-center justify-center rounded-sm hover:bg-accent tx-bg font-mono text-[11px] font-semibold tabular-nums min-w-[2.6rem]"
        >
          {Math.round(zoom * 100)}%
        </button>
        <button
          data-testid="zoom-in"
          onClick={() => setZoom((z) => Math.min(3, +(z + 0.1).toFixed(2)))}
          title={t("zoom_in")}
          className="h-8 w-8 flex items-center justify-center rounded-sm hover:bg-accent tx-bg"
        >
          <ZoomIn className="w-4 h-4" strokeWidth={1.75} />
        </button>
      </div>

      {/* Manual product lookup — works even for image-only PDFs where the
          double-click auto-detection can't find item numbers in a text layer. */}
      <div className="w-px h-6 bg-border mx-1 flex-shrink-0" />
      <ProductLookupInput onSubmit={onLookupProduct} />

      {/* Page-level operations — available on any file (backend
          auto-converts non-PDF uploads to PDF on first op). */}
      <div className="w-px h-6 bg-border mx-1 flex-shrink-0" />
      <button
        data-testid="tool-add-page"
        onClick={onAddPage}
        title="新增一页 (追加空白页到末尾)"
        className="h-8 w-8 flex items-center justify-center rounded-sm hover:bg-accent tx-bg flex-shrink-0"
      >
        <FilePlus className="w-4 h-4" strokeWidth={1.75} />
      </button>
      <button
        data-testid="tool-toggle-pages"
        onClick={onTogglePageRail}
        title="页面缩略图"
        className={`h-8 w-8 flex items-center justify-center rounded-sm tx-bg flex-shrink-0 ${
          pageRailOpen ? "bg-primary text-primary-foreground" : "hover:bg-accent"
        }`}
      >
        <Layers className="w-4 h-4" strokeWidth={1.75} />
      </button>
      <button
        data-testid="tool-compress"
        onClick={onOpenCompress}
        title="压缩文档"
        className="h-8 w-8 flex items-center justify-center rounded-sm hover:bg-accent tx-bg flex-shrink-0"
      >
        <Boxes className="w-4 h-4" strokeWidth={1.75} />
      </button>

      <div className="flex-1 min-w-1" />

      {/* Focus mode */}
      {onToggleFocus && (
        <button
          data-testid={focus ? "focus-exit-btn" : "focus-enter-btn"}
          onClick={onToggleFocus}
          title={`${t("focus_mode")} (F)`}
          className="h-8 w-8 flex items-center justify-center rounded-sm hover:bg-accent tx-bg flex-shrink-0"
        >
          <Maximize2 className="w-4 h-4" strokeWidth={1.75} />
        </button>
      )}

      {/* Share / Export (only when file loaded and not in focus) */}
      {!focus && onShare && (
        <button
          data-testid="share-link-btn"
          onClick={onShare}
          title={t("share")}
          className="h-8 w-8 flex items-center justify-center rounded-sm hover:bg-accent tx-bg flex-shrink-0"
        >
          <Share2 className="w-4 h-4" strokeWidth={1.75} />
        </button>
      )}
      {!focus && onExport && (
        <button
          data-testid={WORKSPACE.exportBtn}
          onClick={onExport}
          title={t("export_annotated")}
          className="h-8 px-2 flex items-center gap-1 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 tx-bg text-xs flex-shrink-0"
        >
          <Download className="w-4 h-4" strokeWidth={1.75} />
          <span className="hidden lg:inline">{t("export_annotated")}</span>
        </button>
      )}

      {/* More menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="tool-more"
            title={t("more")}
            className="h-8 w-8 flex items-center justify-center rounded-sm hover:bg-accent tx-bg flex-shrink-0"
          >
            <MoreHorizontal className="w-4 h-4" strokeWidth={1.75} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[220px]">
          <DropdownMenuLabel className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            {t("more_tools")}
          </DropdownMenuLabel>
          <div className="flex items-center gap-1 px-2 py-1">
            {overflowTools.map((tt) => toolBtn(tt, "h-8 w-8"))}
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            {t("stroke_width")}
          </DropdownMenuLabel>
          <div className="px-2 py-1">
            <input
              data-testid="stroke-width"
              type="range"
              min="1"
              max="10"
              value={strokeWidth}
              onChange={(e) => setStrokeWidth(Number(e.target.value))}
              className="w-full"
            />
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="fit-width" onClick={fitWidth} className="text-xs cursor-pointer gap-2">
            <MoveHorizontal className="w-3.5 h-3.5" strokeWidth={1.75} />
            {t("fit_width")}
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="fit-height" onClick={fitHeight} className="text-xs cursor-pointer gap-2">
            <MoveVertical className="w-3.5 h-3.5" strokeWidth={1.75} />
            {t("fit_height")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="tool-delete"
            onClick={onDelete}
            disabled={!hasSelection}
            className="text-xs cursor-pointer gap-2"
          >
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
            {t("tool_delete_selection")}
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid="tool-clear"
            onClick={onClearAll}
            className="text-xs cursor-pointer gap-2 text-destructive focus:text-destructive"
          >
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
            {t("tool_clear")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// ---------- Page + SVG overlay ----------
function PageWithAnnotations({
  PageComp,
  pageNumber,
  width,
  tool,
  color,
  strokeWidth,
  shapes,
  onCommit,
  selectedId,
  setSelectedId,
  fileId,
  onFirstPageRender,
  onItemLookup,
}) {
  const [dims, setDims] = useState(null); // { w, h } in px at render size
  const containerRef = useRef(null);
  const pageShapes = useMemo(
    () => shapes.filter((s) => (s.page || 1) === pageNumber),
    [shapes, pageNumber]
  );

  // After the PDF.js text layer renders, walk its spans and flag those whose
  // text looks like an item number so we can show product info when the user
  // double-clicks the number.
  const onTextLayerRender = () => {
    const el = containerRef.current;
    if (!el) return;
    setTimeout(() => {
      const layer = el.querySelector(".react-pdf__Page__textContent");
      if (!layer) return;
      // Keep the text layer transparent + click-through by default; only
      // flagged item-number spans get pointer events & a visual hint.
      layer.style.opacity = "0";
      layer.style.pointerEvents = "none";
      layer.style.zIndex = "3";
      const spans = layer.querySelectorAll("span");
      spans.forEach((span) => {
        const txt = (span.textContent || "").trim();
        if (!txt || txt.length > 80) return;
        ITEM_NUMBER_REGEX.lastIndex = 0;
        const m = ITEM_NUMBER_REGEX.exec(txt);
        if (!m) return;
        span.style.pointerEvents = "auto";
        span.style.cursor = "pointer";
        span.style.opacity = "1";
        span.style.color = "transparent";
        span.setAttribute("data-item-number", m[1]);
        span.setAttribute("title", "双击查看产品资料");
        // Discreet dashed underline so users know the number is interactive.
        span.style.borderBottom = "1.5px dashed rgba(47,124,255,0.6)";
      });
      // Delegate double-click for product lookup. Stop propagation so the
      // event doesn't bubble into the annotation SVG below.
      layer.ondblclick = (e) => {
        const s = e.target.closest("span[data-item-number]");
        if (!s) return;
        e.preventDefault();
        e.stopPropagation();
        const rect = s.getBoundingClientRect();
        onItemLookup?.(s.getAttribute("data-item-number"), rect);
      };
    }, 100);
  };

  return (
    <div
      ref={containerRef}
      className="relative shadow-md bg-white"
      data-testid={`preview-page-${pageNumber}`}
      style={dims ? { width: dims.w, height: dims.h } : undefined}
    >
      <PageComp
        pageNumber={pageNumber}
        width={width}
        renderTextLayer={true}
        renderAnnotationLayer={false}
        onGetTextSuccess={onTextLayerRender}
        onRenderSuccess={(page) => {
          const w = page.width || width;
          const h = page.height || width * 1.4;
          setDims({ w, h });
          if (onFirstPageRender) onFirstPageRender(w, h);
        }}
      />
      {dims && (
        <AnnotationLayer
          pageNumber={pageNumber}
          width={dims.w}
          height={dims.h}
          tool={tool}
          color={color}
          strokeWidth={strokeWidth}
          shapes={pageShapes}
          allShapes={shapes}
          onCommit={onCommit}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          fileId={fileId}
        />
      )}
    </div>
  );
}

function AnnotationLayer({
  pageNumber,
  width,
  height,
  tool,
  color,
  strokeWidth,
  shapes,
  allShapes,
  onCommit,
  selectedId,
  setSelectedId,
  fileId,
}) {
  const { t } = useTranslation();
  const svgRef = useRef(null);
  const imgInputRef = useRef(null);
  const [pendingImagePos, setPendingImagePos] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [drawing, setDrawing] = useState(null);
  const [drag, setDrag] = useState(null);
  const [resize, setResize] = useState(null);
  // { mode: 'create'|'edit', x, y, text, fontSize, targetId } while modal is open
  const [textEditor, setTextEditor] = useState(null);

  const openTextEditorForCreate = (p) => {
    setTextEditor({
      mode: "create",
      x: p.x,
      y: p.y,
      text: "",
      fontSize: 18,
      bold: false,
      italic: false,
      underline: false,
      align: "left",
      color: "#2563eb",
    });
  };
  const openTextEditorForEdit = (target) => {
    setTextEditor({
      mode: "edit",
      x: target.x,
      y: target.y,
      text: target.text || "",
      fontSize: target.fontSize || 18,
      bold: !!target.bold,
      italic: !!target.italic,
      underline: !!target.underline,
      align: target.align || "left",
      color: target.color || color,
      targetId: target.id,
    });
  };
  const commitTextEditor = (payload) => {
    const trimmed = (payload.text ?? "").replace(/\r\n?/g, "\n").replace(/[ \t]+$/gm, "");
    if (!trimmed.trim()) {
      if (textEditor?.mode === "edit" && textEditor.targetId) {
        onCommit(allShapes.filter((s) => s.id !== textEditor.targetId));
      }
      setTextEditor(null);
      return;
    }
    const extras = {
      fontSize: payload.fontSize,
      bold: !!payload.bold,
      italic: !!payload.italic,
      underline: !!payload.underline,
      align: payload.align || "left",
      color: payload.color || color,
    };
    if (textEditor.mode === "create") {
      const id = uid();
      onCommit([
        ...allShapes,
        {
          id,
          page: pageNumber,
          type: "text",
          text: trimmed,
          x: textEditor.x,
          y: textEditor.y,
          ...extras,
        },
      ]);
      setSelectedId(id);
    } else {
      onCommit(
        allShapes.map((s) =>
          s.id === textEditor.targetId ? { ...s, text: trimmed, ...extras } : s
        )
      );
    }
    setTextEditor(null);
  };

  const getRel = (e) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    // Both x and y are stored as fractions of the page WIDTH so that they
    // multiply against the viewBox's W=1000 unit correctly. The viewBox
    // height is (h/w)*1000, so a screen y of `py` maps to viewBox y of
    // `py * 1000 / rect.width` — i.e. p.y = py / rect.width, not rect.height.
    // Using rect.height here caused all shapes to appear offset from the
    // click point on non-square pages (which is almost every PDF).
    return {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.width,
    };
  };

  // Hit-test: return the topmost text shape at point p on the current page (or null).
  const hitTestText = (p) => {
    // Iterate in reverse so the top-most (last-drawn) text wins.
    for (let i = allShapes.length - 1; i >= 0; i--) {
      const s = allShapes[i];
      if (s.type !== "text" || (s.page || 1) !== pageNumber) continue;
      const lines = String(s.text || "").split("\n");
      const fs = s.fontSize || 18;
      const h = (fs * 1.25 * lines.length) / (width || 1000);
      // CJK-aware width: full-width glyphs (~fs) vs ASCII (~0.55 * fs).
      const longestPx = lines.reduce((max, ln) => {
        let w = 0;
        for (const ch of ln) w += ch.charCodeAt(0) > 0x7F ? fs : fs * 0.55;
        return Math.max(max, w);
      }, 0);
      const w = Math.min(0.95, longestPx / (width || 1000));
      const pad = 8 / (width || 1000); // small hit-tolerance
      if (
        p.x >= s.x - pad &&
        p.x <= s.x + w + pad &&
        p.y >= s.y - fs / (width || 1000) - pad &&
        p.y <= s.y + h + pad
      ) {
        return s;
      }
    }
    return null;
  };

  const onPointerDown = (e) => {
    // Ignore multi-touch events — those are handled by the pinch-zoom
    // listener on the parent container. This prevents the first finger from
    // capturing pointer events and starting a phantom drawing when the user
    // actually wants to pinch.
    if (e.pointerType === "touch" && !e.isPrimary) return;
    const p = getRel(e);
    if (tool === "select" || tool === "eraser") return; // handled by shape click
    if (tool === "text-edit") {
      // Dedicated "edit-existing-text" tool: hit-test the current page, open
      // the editor if we hit a text annotation; otherwise ignore the click.
      const hit = hitTestText(p);
      if (hit) openTextEditorForEdit(hit);
      return;
    }
    if (tool === "text") {
      openTextEditorForCreate(p);
      return;
    }
    if (tool === "image") {
      // Remember where the user tapped so we can insert the image at that
      // point once the file picker returns.
      setPendingImagePos(p);
      imgInputRef.current?.click();
      return;
    }
    const id = uid();
    let shape;
    if (tool === "rect") {
      shape = { id, page: pageNumber, type: "rect", color, stroke: strokeWidth, x: p.x, y: p.y, w: 0, h: 0 };
    } else if (tool === "ellipse") {
      shape = { id, page: pageNumber, type: "ellipse", color, stroke: strokeWidth, x: p.x, y: p.y, w: 0, h: 0 };
    } else if (tool === "arrow") {
      shape = { id, page: pageNumber, type: "arrow", color, stroke: strokeWidth, x1: p.x, y1: p.y, x2: p.x, y2: p.y };
    } else if (tool === "pen") {
      shape = { id, page: pageNumber, type: "pen", color, stroke: strokeWidth, points: [[p.x, p.y]] };
    }
    if (shape) {
      setDrawing(shape);
      e.currentTarget.setPointerCapture?.(e.pointerId);
    }
  };

  const onPointerMove = (e) => {
    if (!drawing && !drag && !resize) return;
    const p = getRel(e);
    if (drawing) {
      if (drawing.type === "rect" || drawing.type === "ellipse") {
        setDrawing({ ...drawing, w: p.x - drawing.x, h: p.y - drawing.y });
      } else if (drawing.type === "arrow") {
        setDrawing({ ...drawing, x2: p.x, y2: p.y });
      } else if (drawing.type === "pen") {
        setDrawing({ ...drawing, points: [...drawing.points, [p.x, p.y]] });
      }
    } else if (resize) {
      const dx = p.x - resize.startX;
      const dy = p.y - resize.startY;
      const { orig, handle } = resize;
      let x = orig.x, y = orig.y, w = orig.w, h = orig.h;
      // Compute new geometry per handle. Constrain to a minimum size so the
      // handles never collapse onto each other.
      const MIN = 0.02;
      if (handle.includes("e")) w = Math.max(MIN, orig.w + dx);
      if (handle.includes("s")) h = Math.max(MIN, orig.h + dy);
      if (handle.includes("w")) {
        w = Math.max(MIN, orig.w - dx);
      }
      if (handle.includes("n")) {
        h = Math.max(MIN, orig.h - dy);
      }
      // Proportional resize when Shift is held on a corner handle.
      const isCorner = handle.length === 2;
      if (e.shiftKey && isCorner && orig.w > 0 && orig.h > 0) {
        const aspect = orig.h / orig.w;
        // Whichever axis moved further (proportionally) drives the resize.
        if (Math.abs(dx) / orig.w >= Math.abs(dy) / orig.h) {
          h = Math.max(MIN, w * aspect);
        } else {
          w = Math.max(MIN, h / aspect);
        }
      }
      // Re-anchor after all size computations so w-/n- handles stay pinned to
      // the opposite edge even under the aspect lock.
      if (handle.includes("w")) x = orig.x + (orig.w - w);
      if (handle.includes("n")) y = orig.y + (orig.h - h);
      const next = allShapes.map((s) => (s.id === resize.id ? { ...s, x, y, w, h } : s));
      resize._preview = next;
      setResize({ ...resize, tick: (resize.tick || 0) + 1 });
    } else if (drag) {
      const dx = p.x - drag.startX;
      const dy = p.y - drag.startY;
      const next = allShapes.map((s) => {
        if (s.id !== drag.id) return s;
        if (s.type === "rect" || s.type === "ellipse" || s.type === "image") {
          return { ...s, x: drag.orig.x + dx, y: drag.orig.y + dy };
        }
        if (s.type === "arrow") {
          return {
            ...s,
            x1: drag.orig.x1 + dx,
            y1: drag.orig.y1 + dy,
            x2: drag.orig.x2 + dx,
            y2: drag.orig.y2 + dy,
          };
        }
        if (s.type === "text") {
          return { ...s, x: drag.orig.x + dx, y: drag.orig.y + dy };
        }
        if (s.type === "pen") {
          return { ...s, points: drag.orig.points.map(([px, py]) => [px + dx, py + dy]) };
        }
        return s;
      });
      // Live update without pushing history until pointer up
      onCommit._live = true; // marker (ignored)
      // Update via commit on drop only, temporarily mutate via a state pass:
      // We'll dispatch commit only at end. For live preview, use a temp shape state:
      drag._preview = next;
      // Force re-render by updating a token
      setDrag({ ...drag, tick: (drag.tick || 0) + 1 });
    }
  };

  const onPointerUp = (e) => {
    if (drawing) {
      let shape = drawing;
      // Discard trivial shapes
      let keep = true;
      if (shape.type === "rect" || shape.type === "ellipse") {
        if (Math.abs(shape.w) < 0.005 && Math.abs(shape.h) < 0.005) keep = false;
        // Normalise negative w/h
        if (shape.w < 0) {
          shape = { ...shape, x: shape.x + shape.w, w: -shape.w };
        }
        if (shape.h < 0) {
          shape = { ...shape, y: shape.y + shape.h, h: -shape.h };
        }
      } else if (shape.type === "arrow") {
        const dx = shape.x2 - shape.x1;
        const dy = shape.y2 - shape.y1;
        if (Math.abs(dx) < 0.005 && Math.abs(dy) < 0.005) keep = false;
      } else if (shape.type === "pen") {
        if ((shape.points || []).length < 2) keep = false;
      }
      if (keep) {
        onCommit([...allShapes, shape]);
        setSelectedId(shape.id);
      }
      setDrawing(null);
    }
    if (drag) {
      if (drag._preview) onCommit(drag._preview);
      setDrag(null);
    }
    if (resize) {
      if (resize._preview) onCommit(resize._preview);
      setResize(null);
    }
  };

  // Begin resizing the currently selected image using a corner/edge handle.
  const onHandlePointerDown = (e, handle) => {
    e.stopPropagation();
    if (tool !== "select") return;
    const target = allShapes.find((s) => s.id === selectedId && s.type === "image");
    if (!target) return;
    const p = getRel(e);
    setResize({
      id: target.id,
      handle,
      startX: p.x,
      startY: p.y,
      orig: { x: target.x, y: target.y, w: target.w, h: target.h },
    });
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onShapeClick = (e, s) => {
    e.stopPropagation();
    if (tool === "eraser") {
      onCommit(allShapes.filter((x) => x.id !== s.id));
      return;
    }
    if (tool === "select") {
      // Single click selects the shape (enabling drag-to-move).
      // Double-click on a text shape opens the editor (see onDoubleClick).
      setSelectedId(s.id);
      return;
    }
    if (tool === "text-edit" && s.type === "text") {
      openTextEditorForEdit(s);
    }
  };

  const onShapePointerDown = (e, s) => {
    if (tool !== "select") return;
    e.stopPropagation();
    setSelectedId(s.id);
    const p = getRel(e);
    setDrag({
      id: s.id,
      startX: p.x,
      startY: p.y,
      orig: { ...s, points: s.points ? s.points.map((pt) => [...pt]) : undefined },
    });
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onDoubleClick = (e) => {
    // Double-click on a text annotation opens the rich text editor.
    // Works regardless of the active tool (both select and text tool).
    if (tool !== "select" && tool !== "text") return;
    const p = getRel(e);
    const target = hitTestText(p);
    if (target) openTextEditorForEdit(target);
  };

  // Compute shapes to render — include the in-progress drawing and drag/resize preview.
  const rendered = (resize?._preview || drag?._preview)
    ? (resize?._preview || drag?._preview).filter((s) => (s.page || 1) === pageNumber)
    : shapes;
  const withDrawing = drawing ? [...rendered, drawing] : rendered;
  // Selected image on this page (for resize handles)
  const selectedImage = rendered.find(
    (s) => s.id === selectedId && s.type === "image" && (s.page || 1) === pageNumber
  );

  const cursorFor = () => {
    if (tool === "select") return drag ? "grabbing" : "default";
    if (tool === "eraser") return "not-allowed";
    if (tool === "text") return "text";
    if (tool === "text-edit") return "pointer";
    if (tool === "image") return uploadingImage ? "wait" : "copy";
    return "crosshair";
  };

  // ---- Image annotation upload ----
  const onImageFilePicked = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!f || !fileId) {
      setPendingImagePos(null);
      return;
    }
    const pos = pendingImagePos || { x: 0.1, y: 0.1 };
    setPendingImagePos(null);
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(
        `/files/${fileId}/annotation-image`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      // Read image natural size so the placed rectangle preserves aspect.
      const objectUrl = URL.createObjectURL(f);
      const dims = await new Promise((resolve) => {
        const im = new window.Image();
        im.onload = () => resolve({ w: im.naturalWidth, h: im.naturalHeight });
        im.onerror = () => resolve({ w: 300, h: 200 });
        im.src = objectUrl;
      });
      URL.revokeObjectURL(objectUrl);
      // Default width ~30% of page width, keep aspect
      const defaultW = 0.3;
      const aspect = dims.w > 0 ? dims.h / dims.w : 0.75;
      const shape = {
        id: uid(),
        page: pageNumber,
        type: "image",
        src: data.src,
        name: data.name,
        x: Math.max(0, Math.min(1 - defaultW, pos.x - defaultW / 2)),
        y: Math.max(0, pos.y),
        w: defaultW,
        h: defaultW * aspect,
      };
      onCommit([...allShapes, shape]);
      setSelectedId(shape.id);
      toast.success(t("image_inserted"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingImage(false);
    }
  };

  return (
    <>
    <svg
      ref={svgRef}
      className="absolute inset-0 w-full h-full"
      viewBox={`0 0 1000 ${(height / width) * 1000}`}
      preserveAspectRatio="none"
      style={{ cursor: cursorFor(), touchAction: "none" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onDoubleClick={onDoubleClick}
    >
      <defs>
        {COLORS.map((c) => (
          <marker
            key={c.key}
            id={`arrowhead-${c.key}`}
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M0,0 L10,5 L0,10 z" fill={c.value} />
          </marker>
        ))}
      </defs>
      {withDrawing.map((s) => (
        <ShapeSVG
          key={s.id}
          shape={s}
          selected={s.id === selectedId}
          onPointerDown={(e) => onShapePointerDown(e, s)}
          onClick={(e) => onShapeClick(e, s)}
        />
      ))}
      {/* Resize handles for the currently-selected image annotation */}
      {selectedImage && tool === "select" && (() => {
        const W = 1000;
        const x = selectedImage.x * W;
        const y = selectedImage.y * W;
        const w = Math.abs(selectedImage.w) * W;
        const h = Math.abs(selectedImage.h) * W;
        const HS = 10; // handle side (in viewBox units, matches ~10px)
        const half = HS / 2;
        const handles = [
          { key: "nw", cx: x,           cy: y,           cursor: "nwse-resize" },
          { key: "n",  cx: x + w / 2,   cy: y,           cursor: "ns-resize"   },
          { key: "ne", cx: x + w,       cy: y,           cursor: "nesw-resize" },
          { key: "e",  cx: x + w,       cy: y + h / 2,   cursor: "ew-resize"   },
          { key: "se", cx: x + w,       cy: y + h,       cursor: "nwse-resize" },
          { key: "s",  cx: x + w / 2,   cy: y + h,       cursor: "ns-resize"   },
          { key: "sw", cx: x,           cy: y + h,       cursor: "nesw-resize" },
          { key: "w",  cx: x,           cy: y + h / 2,   cursor: "ew-resize"   },
        ];
        return handles.map(({ key, cx, cy, cursor }) => (
          <rect
            key={key}
            data-testid={`image-handle-${key}`}
            x={cx - half}
            y={cy - half}
            width={HS}
            height={HS}
            fill="#ffffff"
            stroke="#2f7cff"
            strokeWidth={2}
            style={{ cursor }}
            onPointerDown={(e) => onHandlePointerDown(e, key)}
          />
        ));
      })()}
    </svg>
      {textEditor && (
        <TextEditorDialog
          initial={textEditor}
          onCancel={() => setTextEditor(null)}
          onConfirm={commitTextEditor}
        />
      )}
      {/* Hidden file input for the image annotation tool */}
      <input
        ref={imgInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        data-testid="image-annotation-input"
        onChange={onImageFilePicked}
      />
    </>
  );
}

// Convert normalised (0..1) shape to SVG using the 1000-unit width viewBox.
function ShapeSVG({ shape, selected, onPointerDown, onClick }) {
  const s = shape;
  const W = 1000; // viewBox width unit
  const aspect = 1; // heights use px directly via ((h/w)*1000)
  // We compute h scaling later via viewBox already set on parent
  const stroke = s.color || "#111";
  const sw = (s.stroke || 3) * 1.4;
  const common = {
    stroke,
    strokeWidth: sw,
    fill: "none",
    onPointerDown,
    onClick,
    style: { cursor: "pointer" },
    "data-testid": `shape-${s.id}`,
  };
  const selectionStyle = selected
    ? { filter: `drop-shadow(0 0 0 rgba(0,0,0,0)) drop-shadow(0 0 4px rgba(30,100,255,0.9))` }
    : {};
  if (s.type === "rect") {
    const x = Math.min(s.x, s.x + s.w) * W;
    const y = Math.min(s.y, s.y + s.h) * W;
    const w = Math.abs(s.w) * W;
    const h = Math.abs(s.h) * W;
    return <rect x={x} y={y} width={w} height={h} {...common} style={{ ...common.style, ...selectionStyle }} />;
  }
  if (s.type === "ellipse") {
    const cx = (s.x + s.w / 2) * W;
    const cy = (s.y + s.h / 2) * W;
    const rx = Math.abs(s.w / 2) * W;
    const ry = Math.abs(s.h / 2) * W;
    return <ellipse cx={cx} cy={cy} rx={rx} ry={ry} {...common} style={{ ...common.style, ...selectionStyle }} />;
  }
  if (s.type === "arrow") {
    const colorKey = COLORS.find((c) => c.value === s.color)?.key || "black";
    return (
      <line
        x1={s.x1 * W}
        y1={s.y1 * W}
        x2={s.x2 * W}
        y2={s.y2 * W}
        markerEnd={`url(#arrowhead-${colorKey})`}
        {...common}
        style={{ ...common.style, ...selectionStyle }}
      />
    );
  }
  if (s.type === "pen") {
    if (!s.points?.length) return null;
    const d = s.points.reduce(
      (acc, [px, py], i) => acc + (i === 0 ? `M${px * W},${py * W}` : ` L${px * W},${py * W}`),
      ""
    );
    return <path d={d} strokeLinecap="round" strokeLinejoin="round" {...common} style={{ ...common.style, ...selectionStyle }} />;
  }
  if (s.type === "image") {
    const x = s.x * W;
    const y = s.y * W;
    const w = Math.abs(s.w || 0.3) * W;
    const h = Math.abs(s.h || 0.2) * W;
    const src = `${process.env.REACT_APP_BACKEND_URL}${s.src}`;
    return (
      <g data-testid={`shape-${s.id}`}>
        <image
          href={src}
          xlinkHref={src}
          x={x}
          y={y}
          width={w}
          height={h}
          preserveAspectRatio="none"
          onPointerDown={onPointerDown}
          onClick={onClick}
          style={{ cursor: "pointer", ...selectionStyle }}
        />
        {selected && (
          <rect
            x={x}
            y={y}
            width={w}
            height={h}
            fill="none"
            stroke="#2f7cff"
            strokeWidth={2}
            strokeDasharray="6 4"
            pointerEvents="none"
          />
        )}
      </g>
    );
  }
  if (s.type === "text") {
    const fs = (s.fontSize || 18) * (W / 1000) * 1.2;
    const lines = String(s.text || "").split("\n");
    const align = s.align || "left";
    const anchor = align === "center" ? "middle" : align === "right" ? "end" : "start";
    // Anchor x offset so alignment lines up with the annotation's origin (s.x)
    const ax =
      align === "center" ? s.x * W + Math.min(0.3, ((Math.max(...lines.map((ln) => ln.length)) || 1) * fs * 0.55) / 2)
      : align === "right" ? s.x * W + Math.min(0.6, ((Math.max(...lines.map((ln) => ln.length)) || 1) * fs * 0.55))
      : s.x * W;
    return (
      <g data-testid={`shape-${s.id}`}>
        <text
          x={ax}
          y={s.y * W + fs * 0.85}
          fill={s.color}
          fontSize={fs}
          fontWeight={s.bold ? 700 : 400}
          fontStyle={s.italic ? "italic" : "normal"}
          textDecoration={s.underline ? "underline" : "none"}
          textAnchor={anchor}
          fontFamily="Calibri, 'Segoe UI', sans-serif"
          onPointerDown={onPointerDown}
          onClick={onClick}
          style={{ cursor: "pointer", userSelect: "none" }}
          pointerEvents="bounding-box"
        >
          {lines.map((ln, i) => (
            <tspan key={i} x={ax} dy={i === 0 ? 0 : fs * 1.25}>
              {ln || " "}
            </tspan>
          ))}
        </text>
      </g>
    );
  }
  return null;
}

// ---- Multi-line text editor dialog ----
const FONT_SIZE_OPTIONS = [12, 14, 16, 18, 22, 28, 36, 48, 64];
const TEXT_COLORS = [
  { key: "red", value: "#e11d48" },
  { key: "green", value: "#059669" },
  { key: "blue", value: "#2563eb" },
  { key: "amber", value: "#d97706" },
  { key: "black", value: "#111111" },
];

function TextEditorDialog({ initial, onCancel, onConfirm }) {
  const { t } = useTranslation();
  const [text, setText] = useState(initial.text || "");
  const [fontSize, setFontSize] = useState(initial.fontSize || 18);
  const [bold, setBold] = useState(!!initial.bold);
  const [italic, setItalic] = useState(!!initial.italic);
  const [underline, setUnderline] = useState(!!initial.underline);
  const [align, setAlign] = useState(initial.align || "left"); // left | center | right
  const [textColor, setTextColor] = useState(initial.color || "#2563eb");
  const taRef = useRef(null);

  useEffect(() => {
    const el = taRef.current;
    if (el) {
      el.focus();
      const len = el.value.length;
      try { el.setSelectionRange(len, len); } catch { /* noop */ }
    }
  }, []);

  const submit = () => onConfirm({ text, fontSize, bold, italic, underline, align, color: textColor });

  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      submit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    } else if ((e.metaKey || e.ctrlKey) && (e.key === "b" || e.key === "B")) {
      e.preventDefault(); setBold((v) => !v);
    } else if ((e.metaKey || e.ctrlKey) && (e.key === "i" || e.key === "I")) {
      e.preventDefault(); setItalic((v) => !v);
    } else if ((e.metaKey || e.ctrlKey) && (e.key === "u" || e.key === "U")) {
      e.preventDefault(); setUnderline((v) => !v);
    }
  };

  const TB_BLUE = "#2f7cff"; // accent blue used for border + active states

  const tbBtn = (active, onClick, testid, title, children) => (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      title={title}
      className="h-8 w-8 flex items-center justify-center rounded-md transition-colors"
      style={{
        background: active ? TB_BLUE : "transparent",
        color: active ? "#ffffff" : "#374151",
      }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "#eef2ff"; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
    >
      {children}
    </button>
  );

  const tbDivider = (
    <span aria-hidden="true" className="mx-1 h-5 w-px" style={{ background: "#d5deec" }} />
  );

  return (
    <div
      data-testid="text-editor-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div
        className="w-[min(720px,96vw)] flex flex-col overflow-hidden bg-white"
        style={{
          border: `2px solid ${TB_BLUE}`,
          borderRadius: "10px",
          boxShadow: "0 20px 60px -20px rgba(47,124,255,0.35), 0 8px 24px -8px rgba(15,23,42,0.15)",
        }}
      >
        {/* Rich formatting toolbar (with close button on the right) */}
        <div
          className="flex flex-wrap items-center gap-0.5 px-3 py-2 flex-shrink-0"
          style={{ background: "#f5f8ff", borderBottom: `1px solid ${TB_BLUE}` }}
        >
          {/* Group 1: Font size dropdown */}
          <div className="relative">
            <select
              data-testid="text-font-size"
              value={fontSize}
              onChange={(e) => setFontSize(Number(e.target.value))}
              className="h-8 pl-2.5 pr-7 rounded-md bg-white text-[13px] text-slate-700 cursor-pointer appearance-none focus:outline-none"
              style={{ border: `1px solid ${TB_BLUE}66`, minWidth: "78px" }}
            >
              {FONT_SIZE_OPTIONS.map((sz) => (
                <option key={sz} value={sz}>{`${t("font_size")} ${sz}`}</option>
              ))}
            </select>
            <svg
              className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2"
              width="10" height="10" viewBox="0 0 10 10" fill="none"
            >
              <path d="M2 3.5L5 6.5L8 3.5" stroke="#64748b" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>

          {tbDivider}

          {/* Group 2: Bold / Italic / Underline */}
          {tbBtn(bold, () => setBold((v) => !v), "text-bold", `${t("bold")} · Ctrl+B`,
            <Bold className="w-4 h-4" strokeWidth={2.5} />)}
          {tbBtn(italic, () => setItalic((v) => !v), "text-italic", `${t("italic")} · Ctrl+I`,
            <Italic className="w-4 h-4" strokeWidth={2.25} />)}
          {tbBtn(underline, () => setUnderline((v) => !v), "text-underline", `${t("underline")} · Ctrl+U`,
            <Underline className="w-4 h-4" strokeWidth={2.25} />)}

          {tbDivider}

          {/* Group 3: Alignment */}
          {tbBtn(align === "left", () => setAlign("left"), "text-align-left", t("align_left"),
            <AlignLeft className="w-4 h-4" strokeWidth={2.25} />)}
          {tbBtn(align === "center", () => setAlign("center"), "text-align-center", t("align_center"),
            <AlignCenter className="w-4 h-4" strokeWidth={2.25} />)}
          {tbBtn(align === "right", () => setAlign("right"), "text-align-right", t("align_right"),
            <AlignRight className="w-4 h-4" strokeWidth={2.25} />)}

          {tbDivider}

          {/* Group 4: Color swatches */}
          <div className="flex items-center gap-1 px-1">
            {TEXT_COLORS.map((c) => {
              const selected = textColor === c.value;
              return (
                <button
                  key={c.key}
                  type="button"
                  data-testid={`text-color-${c.key}`}
                  onClick={() => setTextColor(c.value)}
                  title={t(`color_${c.key}`) || c.key}
                  className="h-5 w-5 rounded-full transition-transform"
                  style={{
                    background: c.value,
                    boxShadow: selected
                      ? `0 0 0 2px #ffffff, 0 0 0 3.5px ${TB_BLUE}`
                      : "0 0 0 1px rgba(15,23,42,0.15)",
                    transform: selected ? "scale(1.08)" : "scale(1)",
                  }}
                />
              );
            })}
          </div>

          {/* Close button (moved into the toolbar row) */}
          <button
            data-testid="text-editor-close"
            onClick={onCancel}
            className="ml-auto h-7 w-7 flex items-center justify-center rounded-full hover:bg-white transition-colors text-slate-500"
            title={t("cancel")}
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        {/* Content editing area — compact, no preview, no hint */}
        <div className="bg-white px-5 py-3">
          <textarea
            ref={taRef}
            data-testid="text-editor-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            rows={3}
            style={{
              fontSize: `${fontSize}px`,
              fontWeight: bold ? 700 : 400,
              fontStyle: italic ? "italic" : "normal",
              textDecoration: underline ? "underline" : "none",
              textAlign: align,
              color: textColor,
              lineHeight: 1.4,
            }}
            className="w-full px-1 py-1 bg-white border-0 resize-none outline-none min-h-[5.5rem]"
          />
        </div>

        {/* Footer */}
        <div
          className="px-4 py-2.5 flex items-center justify-end gap-2 flex-shrink-0"
          style={{ background: "#f5f8ff", borderTop: `1px solid ${TB_BLUE}22` }}
        >
          <button
            data-testid="text-editor-cancel"
            onClick={onCancel}
            className="h-9 px-4 text-[13px] font-medium rounded-md bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 transition-colors"
          >
            {t("cancel")}
          </button>
          <button
            data-testid="text-editor-confirm"
            onClick={submit}
            className="h-9 px-5 text-[13px] font-semibold rounded-md text-white transition-colors"
            style={{ background: TB_BLUE }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#1f6aef"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = TB_BLUE; }}
          >
            {t("confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

